import {detailedDiff} from 'deep-object-diff';
import type {Either} from '#lit/types/Either.js';
import {isLeft, isRight} from '#lit/types/Either.js';
import type {Nullable} from '#lit/types/general.js';
import {debounce} from '#utils/debounce.js';
import type {
  ErrorCallback,
  SideEffects,
} from '#lit/types/builders/audience_builder/services.js';
import type {Audience, AudienceTemplate} from '#lit/types/audiences.js';
import type {DatasetGroup} from '#lit/types/datasets.js';
import type {RequestManagerError} from '../request_manager/errors.js';
import {
  AbortedRequestError,
  DuplicatedRequestError,
} from '../request_manager/errors.js';
import {
  ABRequestKeys,
  ABRequestManager,
} from '../request_manager/request_manager.js';

export type AudienceInsightsServerResponse = {
  size: Nullable<number>;
  revenue: Nullable<number>;
};

export type InsightsServiceResponse = {
  data: AudienceInsightsServerResponse;
  sql: string;
};

export type InsightsServiceError = RequestManagerError;

// How long requests are debounce for.
export const DEBOUNCE_MS = 300;

const ignorableErrorNames = [
  DuplicatedRequestError.errorName,
  AbortedRequestError.errorName,
];

export class InsightsService {
  requestInsights = debounce(this.request, DEBOUNCE_MS);

  private requestManager = new ABRequestManager();
  private previousPayloadGraph = {};

  private handleRequestInsightsFail(
    error: RequestManagerError,
    callback: ErrorCallback<RequestManagerError>
  ): void {
    if (ignorableErrorNames.includes(error.name)) return;
    console.error(error);
    callback(error);
  }

  private async request(
    payload: {
      graph: object;
      audience_id?: string;
      selected_dataset_id?: string;
    },
    callbacks?: SideEffects<RequestManagerError, InsightsServiceResponse>
  ): Promise<Either<RequestManagerError, InsightsServiceResponse>> {
    if (window.__DEBUG__) {
      const difference = detailedDiff(this.previousPayloadGraph, payload.graph);

      console.log('difference', difference);
    }
    this.previousPayloadGraph = payload.graph;

    const response = await this.requestManager.execute<InsightsServiceResponse>(
      ABRequestKeys.INSIGHTS,
      payload
    );

    if (callbacks) {
      if (isLeft(response)) {
        this.handleRequestInsightsFail(response.value, callbacks.onError);
      }

      if (isRight(response)) {
        callbacks.onSuccess(response.value);
      }
    }

    return response;
  }

  setup(props: {
    audience?: Audience | AudienceTemplate;
    datasetGroup?: DatasetGroup;
  }): void {
    if (props.audience) {
      this.requestManager.setAudience(props.audience);
    }
    if (props.datasetGroup) {
      this.requestManager.setDatasetGroup(props.datasetGroup);
    }
  }
}
